// UX-001: User-friendly error message utilities

// Map of technical error codes to user-friendly messages
export const errorMessageMap = {
  // Auth errors
  'AUTH_INVALID_CREDENTIALS': 'Invalid email or password. Please try again.',
  'AUTH_USER_NOT_FOUND': 'No account found with this email address.',
  'AUTH_USER_INACTIVE': 'Your account has been deactivated. Please contact support.',
  'AUTH_TOKEN_EXPIRED': 'Your session has expired. Please log in again.',
  'AUTH_TOKEN_INVALID': 'Invalid session. Please log in again.',
  'AUTH_TOKEN_REQUIRED': 'Please log in to continue.',
  'AUTH_ACCOUNT_LOCKED': 'Your account has been locked due to too many failed attempts. Please try again later.',
  'AUTH_OTP_INVALID': 'Invalid OTP. Please check and try again.',
  'AUTH_OTP_EXPIRED': 'OTP has expired. Please request a new one.',
  'AUTH_OTP_RATE_LIMIT': 'Too many OTP requests. Please try again later.',

  // Network errors
  'NETWORK_ERROR': 'Unable to connect to the server. Please check your internet connection.',
  'TIMEOUT_ERROR': 'The request took too long. Please try again.',
  'SERVER_ERROR': 'Something went wrong on our end. Please try again later.',

  // Validation errors
  'VALIDATION_REQUIRED': 'Please fill in all required fields.',
  'VALIDATION_INVALID_EMAIL': 'Please enter a valid email address.',
  'VALIDATION_INVALID_PASSWORD': 'Password must be at least 6 characters.',
  'VALIDATION_PASSWORD_MISMATCH': 'Passwords do not match.',
  'VALIDATION_WEAK_PASSWORD': 'Password is too weak. Use a combination of letters and numbers.',

  // Permission errors
  'PERMISSION_DENIED': 'You do not have permission to perform this action.',
  // Subscription suspended
  'SUBSCRIPTION_SUSPENDED': 'Your subscription is suspended. Please contact support or your administrator to resolve this issue.',
  'PERMISSION_VIEW_DENIED': 'You do not have permission to view this resource.',
  'PERMISSION_CREATE_DENIED': 'You do not have permission to create this resource.',
  'PERMISSION_EDIT_DENIED': 'You do not have permission to edit this resource.',
  'PERMISSION_DELETE_DENIED': 'You do not have permission to delete this resource.',

  // Resource errors
  'RESOURCE_NOT_FOUND': 'The requested resource could not be found.',
  'RESOURCE_ALREADY_EXISTS': 'This resource already exists.',
  'RESOURCE_IN_USE': 'This resource is currently in use and cannot be modified.',

  // Generic fallbacks
  'UNKNOWN_ERROR': 'An unexpected error occurred. Please try again.',
  'OPERATION_FAILED': 'The operation could not be completed. Please try again.',
};

/**
 * Get a user-friendly error message from an error response
 * @param {Object|Error} error - The error object from axios or catch
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyError = (error) => {
  // Handle subscription expiration explicitly - never silent on login
  if (error?.response?.status === 402 || error?.response?.data?.code === 'SUBSCRIPTION_EXPIRED' || error?.response?.data?.code === 'SUBSCRIPTION_SUSPENDED') {
    return error?.response?.data?.message || 'Your subscription has expired. Please select a plan to continue.';
  }

  // Silent restrictions handled globally via modal/overlay
  if (error?.isSilentRestriction || error?.isSilent) {
    return 'IGNORE_SILENT_RESTRICTION';
  }

  // Handle axios response error
  if (error?.response) {
    const { data, status } = error.response;

    // Check for custom error code
    if (data?.code && errorMessageMap[data.code]) {
      return errorMessageMap[data.code];
    }

    // Check for validation errors
    if (data?.errors && Array.isArray(data.errors)) {
      return data.errors[0]?.msg || data.errors[0]?.message || errorMessageMap.VALIDATION_REQUIRED;
    }

    // Check for error message in response
    if (data?.message) {
      // If message is already user-friendly, return it
      if (!data.message.includes('ER_') && !data.message.includes('Sequelize')) {
        return data.message;
      }
    }

    // Handle HTTP status codes
    switch (status) {
      case 400:
        return errorMessageMap.VALIDATION_REQUIRED;
      case 401:
        return errorMessageMap.AUTH_TOKEN_INVALID;
      case 403:
        return errorMessageMap.PERMISSION_DENIED;
      case 404:
        return errorMessageMap.RESOURCE_NOT_FOUND;
      case 409:
        return data?.message || 'A duplicate record already exists.';

      case 422:
        return data?.message || errorMessageMap.VALIDATION_REQUIRED;
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
      case 502:
      case 503:
        return errorMessageMap.SERVER_ERROR;
      default:
        break;
    }
  }

  // Handle network errors
  if (error.code === 'ECONNABORTED') {
    return errorMessageMap.TIMEOUT_ERROR;
  }

  // Handle generic Error objects (e.g. frontend validation) BEFORE network check
  // so plain JS errors don't get swallowed as "Unable to connect"
  if (error instanceof Error && error.message && error.message !== 'Network Error') {
    const message = error.message;
    if (message.includes('ER_') || message.includes('Sequelize') || message.includes('SQL')) {
      return errorMessageMap.OPERATION_FAILED;
    }
    return message;
  }

  if (error.message === 'Network Error' || !error.response) {
    return errorMessageMap.NETWORK_ERROR;
  }

  return errorMessageMap.UNKNOWN_ERROR;
};

/**
 * Check if error is retryable
 * @param {Object} error - The error object
 * @returns {boolean} True if operation can be retried
 */
export const isRetryableError = (error) => {
  if (!error.response) {
    return true; // Network errors are retryable
  }

  const retryableStatus = [408, 429, 500, 502, 503, 504];
  return retryableStatus.includes(error.response.status);
};

export default getUserFriendlyError;
